/**
 * Environment-specific Configuration for Fashion Connect Main App
 */

export type Environment = 'development' | 'production';

export interface EnvironmentConfig {
  name: Environment;
  api: {
    productsApi: string;
    brandsApi: string;
    combinationsApi: string;
    validationApi: string;
  };
  app: {
    url: string;
    name: string;
  };
}

const developmentConfig: EnvironmentConfig = {
  name: 'development',
  api: {
    productsApi: process.env.REACT_APP_PRODUCTS_API || 'https://api.fashcortex.com/api/v1',
    brandsApi: process.env.REACT_APP_BRANDS_API || 'https://api.fashcortex.com/api/v1',
    combinationsApi: process.env.REACT_APP_COMBINATIONS_API || 'https://api.fashcortex.com/api/v1',
    validationApi: process.env.REACT_APP_VALIDATION_API || 'https://api.fashcortex.com/api/v1',
  },
  app: {
    url: 'http://localhost:3000',
    name: 'Fashion Connect (Development)',
  },
};

const productionConfig: EnvironmentConfig = {
  name: 'production',
  api: {
    // Unified Backend - Production
    // Always use api.fashcortex.com for production, never Railway URLs
    productsApi: process.env.REACT_APP_PRODUCTS_API || 'https://api.fashcortex.com/api/v1',
    brandsApi: process.env.REACT_APP_BRANDS_API || 'https://api.fashcortex.com/api/v1',
    combinationsApi: process.env.REACT_APP_COMBINATIONS_API || 'https://api.fashcortex.com/api/v1',
    validationApi: process.env.REACT_APP_VALIDATION_API || 'https://api.fashcortex.com/api/v1',
  },
  app: {
    url: 'https://app.fashcortex.com',
    name: 'Fashion Connect',
  },
};

// Ensure production always uses api.fashcortex.com, not Railway URLs
if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production') {
  // Override any Railway URLs with production domain
  const productionApiUrl = 'https://api.fashcortex.com/api/v1';
  if (productionConfig.api.productsApi.includes('railway.app')) {
    productionConfig.api.productsApi = productionApiUrl;
  }
  if (productionConfig.api.brandsApi.includes('railway.app')) {
    productionConfig.api.brandsApi = productionApiUrl;
  }
  if (productionConfig.api.combinationsApi.includes('railway.app')) {
    productionConfig.api.combinationsApi = productionApiUrl;
  }
  if (productionConfig.api.validationApi.includes('railway.app')) {
    productionConfig.api.validationApi = productionApiUrl;
  }
}

/**
 * Get environment configuration based on REACT_APP_ENV or NODE_ENV
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Check if we're running on production domain
  const isProductionDomain = typeof window !== 'undefined' && 
    (window.location.hostname === 'app.fashcortex.com' || 
     window.location.hostname.includes('fashcortex.com'));
  
  const env = (process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development') as Environment;
  
  // Force production config if running on production domain
  const finalEnv = isProductionDomain ? 'production' : env;
  
  switch (finalEnv) {
    case 'production':
      return productionConfig;
    case 'development':
    default:
      return developmentConfig;
  }
}

export const environmentConfig = getEnvironmentConfig();

